import { useEffect, useReducer, useState, useRef } from 'react'
import { useClientSideTranslation } from 'src/services/i18n'
import { useGetCurrentLanguage } from 'src/store/game'
import type { MarbleGame } from 'src/types/domain/Game'
import { getLatestEvent } from 'src/utils/raceUtils'
import { useGetCurrentBet } from 'src/hooks/useGetCurrentBet'
import RoadIcon from 'src/app/assets/icons/ico-road.svg'
import FlagCheckeredIcon from 'src/app/assets/icons/ico-flag-checkered.svg'
import { useTimer } from 'src/hooks/useTimer'
import { getBettingTimeWindow } from 'src/utils/getBettingTimeWindow'
import { getSettledTimeWindow } from 'src/utils/getSettledTimeWindow'
import { isCrashGameType } from 'src/utils/gameTypeUtils'
import { useVisualCrashStatus } from 'src/components/CrashChipPanel/useVisualCrash'
import { formatCountdownSeconds } from 'src/components/CrashChipPanel/raceCountdown'
import { hardBanner, perfNow } from 'src/utils/hardCloseClock'
import { useBetStore } from 'src/store/bet'
import { RejectionDetails } from 'src/types/domain/AppError'
import {
  ringDashOffset,
  runRingFrames,
  statusCountdownAt,
  type StatusCountdownInput
} from './statusRing'

type Props = Readonly<{
  game: MarbleGame
}>

export function ShowRaceStatus({ game }: Props) {
  const { t } = useClientSideTranslation(useGetCurrentLanguage())

  const SETTLED_COUNTDOWN_TIME = getSettledTimeWindow(game)

  const { currentBet } = useGetCurrentBet()
  const userHasBet = currentBet !== undefined
  const currentRace = game.races[0]
  const latestEvent = getLatestEvent(currentRace.raceEvents)
  const { eventInfo } = useTimer({
    race: currentRace,
    closingSeconds: game.bettingClosingWindow || undefined
  })

  const isCrash = isCrashGameType(game.track.gameType)

  // CRASH GAMES ONLY: the crash EVENT lands seconds before the crash is
  // visible on the viewer's frames. While the visual-crash store is 'armed'
  // (a driver is live and the crash frame hasn't reached this viewer), keep
  // presenting the round as still running instead of "Race ended…".
  // 'undriven' (spectators / other games) falls back to event-driven.
  const visualCrashStatus = useVisualCrashStatus()
  const holdCrashPill =
    isCrash &&
    visualCrashStatus === 'armed' &&
    (latestEvent?.eventType === 'race_crashed' || latestEvent?.eventType === 'race_ended')
  // Drives ONLY the presented pill (message / colour class / icon) — all
  // countdown bookkeeping stays on the real event so nothing else shifts.
  const displayEventType = holdCrashPill ? 'race_started' : latestEvent?.eventType

  const [raceDuration, setRaceDuration] = useState(0)
  const [showMessage, setShowMessage] = useState(true)
  const animationRef = useRef<HTMLDivElement>(null)
  const [finalCountdown, setFinalCountdown] = useState<number | null>(null)

  const raceStartEvent = currentRace.raceEvents.find((event) => event.eventType === 'race_started')
  const raceSettledEvent = currentRace.raceEvents.find(
    (event) => event.eventType === 'race_settled'
  )
  const raceCancelledEvent = currentRace.raceEvents.find(
    (event) => event.eventType === 'race_cancelled'
  )

  const raceEndedEvent = currentRace.raceEvents.find((event) => event.eventType === 'race_ended')

  // Anchor the between-rounds countdown on the EARLIEST marker present so the
  // pill and the crash centre ring (getRaceCountdownInfo) stay on one clock:
  // race_ended fires post_hold after the viewer's crash, race_settled follows.
  // Anchoring the pill on race_settled while the ring anchored on race_ended
  // would make the two disagree — and the ring jump — for that gap.
  //
  // CRASH ONLY, for the same reason as betweenRoundsAnchorsOnEnded below.
  const finalEvent =
    latestEvent?.eventType === 'race_cancelled'
      ? raceCancelledEvent
      : ((isCrash ? raceEndedEvent : undefined) ?? raceSettledEvent)

  // THE countdown, read now (`statusRing.ts`): a hard round's betting count
  // runs to the board's lock, then the short count of X, on this page's own
  // arrival clock (DESIGN §6 "Countdowns"); a hard round this page did not
  // hear open counts the round's own window off its stamp; every other round,
  // today's count. It reads the shared, pure derivation (`raceCountdown.ts`)
  // the crash centre display reads too, so both surfaces keep one clock.
  const countdownInput: StatusCountdownInput = {
    race: currentRace,
    previousRace: game.races[1],
    bettingWindowSec: getBettingTimeWindow(game),
    settledWindowSec: SETTLED_COUNTDOWN_TIME,
    finalCountdown,
    // CRASH ONLY — the marble engine still leaves a 2s race_ended ->
    // race_settled gap, so anchoring there fleet-wide would start every
    // marble game's between-rounds countdown 2s early.
    betweenRoundsAnchorsOnEnded: isCrash
  }
  const perfAt = perfNow()
  const status = statusCountdownAt(countdownInput, { perf: perfAt, wall: Date.now() })
  const hardClock = status.hardClock

  // A hard round's banner (rev 4.1, owner 10-27): stage 2 from the lock, and
  // "Last chance to bet!" only before it. Null on every other round.
  const roundId = currentRace.uuid
  const betInFlight = useBetStore((state) =>
    Object.values(state.unacceptedBets).some((bet) => bet.raceId === roundId)
  )
  const notInTime = useBetStore(
    (state) =>
      state.lastRejection?.raceId === roundId &&
      state.lastRejection.detail === RejectionDetails.NotInTime
  )
  const hardStage = hardClock
    ? hardBanner({ clock: hardClock, now: perfAt, betInFlight, hasBet: userHasBet, notInTime })
    : null
  const hardBetting =
    hardStage !== null &&
    (displayEventType === 'race_ready' || displayEventType === 'race_starting')

  useEffect(() => {
    let intervalId: NodeJS.Timeout | null = null

    if (
      ((isCrash && latestEvent?.eventType === 'race_ended') ||
        latestEvent?.eventType === 'race_settled' ||
        latestEvent?.eventType === 'race_cancelled') &&
      finalEvent
    ) {
      const eventTime = new Date(finalEvent.eventTimestamp).getTime()
      const elapsed = Math.floor((Date.now() - eventTime) / 1000)
      const initialRemaining = Math.max(SETTLED_COUNTDOWN_TIME - elapsed, 0)
      setFinalCountdown(initialRemaining)

      if (initialRemaining > 0) {
        intervalId = setInterval(() => {
          setFinalCountdown((prev) => {
            if (prev === null || prev <= 0) {
              if (intervalId) clearInterval(intervalId)
              return 0
            }
            return prev - 1
          })
        }, 1000)
      }
    } else {
      setFinalCountdown(null)
    }

    return () => {
      if (intervalId) clearInterval(intervalId)
    }
  }, [latestEvent?.eventType, finalEvent])

  useEffect(() => {
    setShowMessage(true)
    const timer = setTimeout(() => {
      setShowMessage(false)
    }, SETTLED_COUNTDOWN_TIME * 1000)
    return () => {
      clearTimeout(timer)
    }
  }, [latestEvent?.eventType])

  useEffect(() => {
    let intervalId: NodeJS.Timeout | null = null

    if (latestEvent?.eventType === 'race_started' && raceStartEvent) {
      const startTime = new Date(raceStartEvent.eventTimestamp).getTime()
      const initialDuration = Math.floor((Date.now() - startTime) / 1000)
      setRaceDuration(initialDuration)

      intervalId = setInterval(() => {
        setRaceDuration((prev) => prev + 1)
      }, 1000)
    }

    return () => {
      if (intervalId) clearInterval(intervalId)
    }
  }, [latestEvent?.eventType, raceStartEvent])

  const formatTime = formatCountdownSeconds

  const countdownInfo = status.info

  const getCurrentTime = () => {
    if (latestEvent?.eventType === 'race_started') {
      return raceDuration
    } else if (finalCountdown !== null) {
      return finalCountdown
    }

    return countdownInfo.current
  }

  const shouldPulse = hardBetting
    ? hardStage === 'last-chance'
    : getCurrentTime() <= 3 &&
      ['race_starting', 'race_settled'].includes(latestEvent?.eventType || '')

  const renderMessage = () => {
    // A hard round: "Last chance to bet!" only in the last seconds before the
    // lock; from the lock, stage 2, whichever row is latest and whether or not
    // this page heard race_ready.
    if (hardBetting) {
      switch (hardStage) {
        case 'placing':
          return t('Placing your bets…')
        case 'confirming':
          return t('Confirming with your casino')
        case 'placed':
          return `✓ ${t('Your bet has been placed')}`
        case 'not-in-time':
          return t('toast.errors.notInTime')
        case 'locked':
          return t('Locked')
        case 'last-chance':
          return userHasBet ? t('Your bet has been placed') : t('Last chance to bet!')
        default:
          return userHasBet ? t('Your bet has been placed') : t('Place your bets now!')
      }
    }
    switch (displayEventType) {
      case 'race_scheduled':
        return eventInfo
          ? `${t('Race Scheduled.')} ${' '}${t('Live in')} ${eventInfo.getTime()}`
          : t('Race Scheduled.')
      case 'race_ready':
        return userHasBet ? t('Your bet has been placed') : t('Place your bets now!')
      case 'race_starting':
        return userHasBet ? t('Race starting shortly') : t('Last chance to bet!')
      case 'race_started':
        return userHasBet ? t('Race started. Good luck!') : t('Race started')
      case 'race_crashed':
      case 'race_ended':
        return t('Race ended. Processing results')
      case 'race_settled':
        return t('Next Race is starting soon')
      case 'race_cancelled':
        return t('Race cancelled')
      default:
        return t('Updating...')
    }
  }

  // CRASH GAMES ONLY (v7): the pill shows only race_scheduled and
  // race_cancelled, and no ring on race_cancelled (see below).
  const hiddenForCrash =
    isCrash &&
    latestEvent?.eventType !== 'race_scheduled' &&
    latestEvent?.eventType !== 'race_cancelled'
  const suppressCountdownIconForCrash = isCrash && latestEvent?.eventType === 'race_cancelled'

  // The ring is drawn every animation frame while a count runs, from the same
  // derivation at the frame's clocks: it moves continuously, is full exactly at
  // the count's end, and snaps (never sweeps back) when a count restarts. The
  // pill re-renders on the frame its number or a hard round's stage changes,
  // rather than on the 1 s tick above, which runs out of step with the round.
  const ringRef = useRef<SVGCircleElement>(null)
  const countdownInputRef = useRef(countdownInput)
  countdownInputRef.current = countdownInput
  const statusKeyRef = useRef(status.key)
  statusKeyRef.current = status.key
  const [, renderRingFrame] = useReducer((frame: number) => frame + 1, 0)
  const ringRunning = status.span !== null && !hiddenForCrash && !suppressCountdownIconForCrash
  useEffect(() => {
    if (!ringRunning) return undefined
    return runRingFrames({
      read: () => statusCountdownAt(countdownInputRef.current, { perf: perfNow(), wall: Date.now() }),
      initialKey: statusKeyRef.current,
      draw: (fill) => {
        const circle = ringRef.current
        if (circle) circle.style.strokeDashoffset = ringDashOffset(fill)
      },
      render: renderRingFrame
    })
  }, [ringRunning])
  // A ring that stands still shows this render's fill, whatever the last frame drew.
  const stillFill = ringRunning ? null : status.ring
  useEffect(() => {
    const circle = ringRef.current
    if (stillFill !== null && circle) circle.style.strokeDashoffset = ringDashOffset(stillFill)
  }, [stillFill])

  const shouldShowNoCountdown =
    ((latestEvent?.eventType === 'race_ready' || latestEvent?.eventType === 'race_starting') &&
      !userHasBet) ||
    (latestEvent?.eventType === 'race_started' && userHasBet)

  // CRASH GAMES ONLY (v7): the centre display (CrashMultiplierDisplay) owns
  // the round narrative — "Place your bets" + betting ring, the multiplier,
  // CRASHED, and the "Next round" ring. The pill renders ONLY the states the
  // centre does not narrate: race_scheduled ("Live in …") and race_cancelled
  // (the cancellation message; its countdown ring is suppressed below since
  // the centre already shows it). Everything else is fully redundant.
  if (hiddenForCrash) {
    return null
  }

  return (
    <div className={`show-race-status ${displayEventType} ${userHasBet ? 'user-has-bet' : ''}`}>
      <div className={`race-status-message ${showMessage ? 'show' : ''}`}>
        <span>{renderMessage()}</span>
      </div>
      {/* CRASH + race_cancelled: the countdown ring is suppressed — the
          centre display's "Next round" ring already shows it. */}
      {!suppressCountdownIconForCrash && (
        <div className={`race-status-icon ${shouldShowNoCountdown ? 'no-show-countdown' : ''}`}>
          <div
            className={`status-countdown ${shouldPulse ? 'pulse-animation' : ''}`}
            ref={animationRef}
          >
            {displayEventType === 'race_started' ? (
              <span className="icon">
                <RoadIcon />
              </span>
            ) : displayEventType === 'race_ended' || displayEventType === 'race_crashed' ? (
              <span className="icon">
                <FlagCheckeredIcon />
              </span>
            ) : displayEventType === 'race_scheduled' ? (
              <div className="status-countdown-number">{eventInfo?.getTime()}</div>
            ) : (
              <div className="status-countdown-number">{formatTime(getCurrentTime())}</div>
            )}
            <svg>
              {/* No CSS transition on the offset: the frames above draw it. */}
              <circle
                cx="20"
                cy="20"
                r="18"
                ref={ringRef}
                style={{ strokeDashoffset: ringDashOffset(status.ring) }}
              />
            </svg>
          </div>
        </div>
      )}
    </div>
  )
}
